import Link from "next/link";
import PhoneInput from "react-phone-number-input";
import flags from "react-phone-number-input/flags";
import "react-phone-number-input/style.css";
import type { Service } from "@/lib/types";
import { useTranslations } from "@/i18n/LocaleProvider";
import PhoneOtpAuth from "@/components/PhoneOtpAuth";
import EmailVerificationField from "@/components/EmailVerificationField";
import { REQUIRE_PHONE_VERIFICATION, SHOW_PRICING } from "@/lib/featureFlags";

export default function DetailsStep({
  service,
  name,
  onNameChange,
  email,
  onEmailChange,
  emailVerified,
  onEmailVerified,
  phone,
  onPhoneChange,
  phoneVerified,
  onPhoneVerified,
  termsAccepted,
  onTermsAcceptedChange,
  marketingOptIn,
  onMarketingOptInChange,
  couponCode,
  onCouponCodeChange,
  loading,
  onSubmit,
}: {
  service: Service;
  name: string;
  onNameChange: (value: string) => void;
  email: string;
  onEmailChange: (value: string) => void;
  emailVerified: boolean;
  onEmailVerified: () => void;
  phone: string;
  onPhoneChange: (value: string) => void;
  phoneVerified: boolean;
  onPhoneVerified: () => void;
  termsAccepted: boolean;
  onTermsAcceptedChange: (value: boolean) => void;
  marketingOptIn: boolean;
  onMarketingOptInChange: (value: boolean) => void;
  couponCode: string;
  onCouponCodeChange: (value: string) => void;
  loading: boolean;
  onSubmit: () => void;
}) {
  const { t } = useTranslations();
  // Phone must be verified to submit at all; email is optional, but if the customer started
  // typing one, it has to be verified too before submit unlocks -- an unverified, half-entered
  // email is worse than no email, since nothing would ever get delivered to it. See
  // featureFlags.ts: with verification off, this just requires the plain fields be filled in --
  // functions/src/callable/createHold.ts still requires a verified phone server-side regardless,
  // so submitting here will fail until REQUIRE_PHONE_VERIFICATION is switched back on.
  const canSubmit = REQUIRE_PHONE_VERIFICATION
    ? termsAccepted && phoneVerified && (email.trim() === "" || emailVerified)
    : termsAccepted && name.trim() !== "" && phone.trim() !== "";
  const depositPercent = service.depositPercent ?? 30;
  const depositAmount = Math.round((service.priceInr * depositPercent) / 100);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="mt-3 space-y-2.5 sm:mt-4 sm:space-y-3"
    >
      {SHOW_PRICING && depositPercent < 100 && (
        <div className="flex justify-center">
          <span className="inline-flex rounded-full bg-accent-2 px-2.5 py-0.5 text-xs font-semibold text-accent-2-foreground">
            {t("home.services.depositBadge", {
              amount: depositAmount.toLocaleString("en-IN"),
            })}
          </span>
        </div>
      )}
      <div className="surface-card space-y-2.5 p-4 sm:space-y-3 sm:p-5">
        <input
          required
          placeholder={t("book.details.namePlaceholder")}
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          className="w-full rounded-lg border border-foreground/20 px-4 py-2.5"
        />

        {REQUIRE_PHONE_VERIFICATION ? (
          <PhoneOtpAuth
            phone={phone}
            onPhoneChange={onPhoneChange}
            onVerified={onPhoneVerified}
            bordered={false}
            initiallyVerified={phoneVerified}
          />
        ) : (
          <div className="space-y-1.5">
            <label htmlFor="book-phone-input" className="text-[0.8rem] font-medium tracking-wide text-foreground/70">
              {t("book.details.phonePlaceholder")}
            </label>
            <PhoneInput
              id="book-phone-input"
              international
              defaultCountry="IN"
              countryCallingCodeEditable={false}
              value={phone}
              onChange={(value) => onPhoneChange(value ?? "")}
              className="phone-input"
              numberInputProps={{ required: true }}
              flags={flags}
            />
          </div>
        )}

        {REQUIRE_PHONE_VERIFICATION ? (
          phoneVerified && (
            <EmailVerificationField
              email={email}
              onEmailChange={onEmailChange}
              onVerified={onEmailVerified}
              initiallyVerified={emailVerified}
            />
          )
        ) : (
          <input
            type="email"
            placeholder={t("book.details.emailPlaceholder")}
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            className="w-full rounded-lg border border-foreground/20 px-4 py-2.5"
          />
        )}

        <input
          placeholder={t("book.details.couponPlaceholder")}
          value={couponCode}
          onChange={(e) => onCouponCodeChange(e.target.value)}
          className="w-full rounded-lg border border-foreground/20 px-4 py-2.5"
        />

        <div className="space-y-1 sm:space-y-1.5">
          <label className="flex items-start gap-3 text-sm">
            <input
              required
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => onTermsAcceptedChange(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              {t("book.details.termsPrefix")}{" "}
              <Link href="/terms" target="_blank" className="text-accent underline">
                {t("book.details.termsLink")}
              </Link>{" "}
              {t("book.details.and")}{" "}
              <Link href="/privacy" target="_blank" className="text-accent underline">
                {t("book.details.privacyLink")}
              </Link>
              .
            </span>
          </label>
          <label className="flex items-start gap-3 text-sm">
            <input
              type="checkbox"
              checked={marketingOptIn}
              onChange={(e) => onMarketingOptInChange(e.target.checked)}
              className="mt-0.5"
            />
            <span>{t("book.details.marketingOptIn")}</span>
          </label>
        </div>

        <p className="text-sm text-muted">{t("book.details.holdNotice")}</p>
      </div>
      <div className="flex justify-center">
        <button
          type="submit"
          disabled={loading || !canSubmit}
          className="rounded-xl bg-accent px-8 py-2.5 font-semibold text-accent-foreground disabled:opacity-50"
        >
          {loading ? t("book.details.submitLoading") : t("book.details.submitIdle")}
        </button>
      </div>
    </form>
  );
}
